/**
 * Formats how long ago a machine status was last known to be true.
 *
 * Used to qualify a stale status - one EDMS synthesised after the machine
 * stopped sending heartbeats - so the age of the reading is visible instead of
 * a months-old state looking as current as a live one.
 *
 * @param asOf - When the status was last known to be true
 * @param now - Reference point, injectable for tests
 * @returns A short relative label, or null when there is no usable timestamp
 *
 * @example
 * formatStatusAge(new Date(Date.now() - 90_000)) // '1m ago'
 * formatStatusAge(new Date('2025-12-10')) // '237d ago'
 */
export function formatStatusAge(
  asOf: Date | null | undefined,
  now: Date = new Date()
): string | null {
  if (!asOf || Number.isNaN(asOf.getTime())) return null;

  const seconds = Math.floor((now.getTime() - asOf.getTime()) / 1000);
  if (seconds < 0) return null;
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
